/**
 * RestockRocket injects a Shopify selling_plan on made-to-order / 0-inventory
 * products. Leadpipe testing kits (and anything with inventoryPolicy CONTINUE)
 * have no selling plan attached in Shopify, so /cart/add.js returns
 * "Cannot apply selling plan to variant".
 *
 * This guard retries the same add without selling_plan / RestockRocket cart
 * attributes, using the native fetch saved before app scripts wrap it.
 */
(function () {
  const nativeFetch =
    window.__omalleyNativeFetch ||
    (typeof window.fetch === 'function' ? window.fetch.bind(window) : null);
  if (!nativeFetch) return;
  if (!window.__omalleyNativeFetch) window.__omalleyNativeFetch = nativeFetch;

  function cartAddUrl(url) {
    return /\/cart\/add(\.js)?(\?|$)/i.test(String(url || ''));
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input === 'object' && input.url) return input.url;
    return '';
  }

  function stripJson(value) {
    if (!value || typeof value !== 'object') return { value, changed: false };
    if (Array.isArray(value)) {
      let changed = false;
      const next = value.map((item) => {
        const inner = stripJson(item);
        if (inner.changed) changed = true;
        return inner.value;
      });
      return { value: next, changed };
    }

    const next = { ...value };
    let changed = false;
    if ('selling_plan' in next) {
      delete next.selling_plan;
      changed = true;
    }
    if (next.items) {
      const inner = stripJson(next.items);
      next.items = inner.value;
      if (inner.changed) changed = true;
    }
    if (next.attributes && typeof next.attributes === 'object') {
      const attrs = { ...next.attributes };
      for (const key of Object.keys(attrs)) {
        if (key.indexOf('__stoq_data_') === 0) {
          delete attrs[key];
          changed = true;
        }
      }
      next.attributes = attrs;
    }
    return { value: next, changed };
  }

  function stripBody(body) {
    if (body == null) return { body, changed: false };

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const next = new FormData();
      let changed = false;
      body.forEach((value, key) => {
        if (key === 'selling_plan' || key === 'selling_plan_id' || key.indexOf('attributes[__stoq_data_') === 0) {
          changed = true;
          return;
        }
        next.append(key, value);
      });
      return { body: next, changed };
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      const next = new URLSearchParams(body);
      let changed = false;
      if (next.has('selling_plan')) {
        next.delete('selling_plan');
        changed = true;
      }
      Array.from(next.keys()).forEach((key) => {
        if (key.indexOf('attributes[__stoq_data_') === 0) {
          next.delete(key);
          changed = true;
        }
      });
      return { body: next, changed };
    }

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        const stripped = stripJson(parsed);
        return {
          body: stripped.changed ? JSON.stringify(stripped.value) : body,
          changed: stripped.changed,
        };
      } catch {
        const params = new URLSearchParams(body);
        if (params.has('selling_plan') || Array.from(params.keys()).some((k) => k.indexOf('attributes[__stoq_data_') === 0)) {
          params.delete('selling_plan');
          Array.from(params.keys()).forEach((key) => {
            if (key.indexOf('attributes[__stoq_data_') === 0) params.delete(key);
          });
          return { body: params.toString(), changed: true };
        }
        return { body, changed: false };
      }
    }

    if (typeof body === 'object') {
      const stripped = stripJson(body);
      return { body: stripped.value, changed: stripped.changed };
    }

    return { body, changed: false };
  }

  function isSellingPlanError(json, status) {
    if (status !== 422 && json?.status !== 422) return false;
    const message = String(json?.description || json?.message || json?.errors || '');
    return /selling plan/i.test(message) || message === 'Cart Error';
  }

  function stripInjectedInputs() {
    document.querySelectorAll('input.restock-rocket-form-input[name="selling_plan"], input.restock-rocket-form-input[name^="attributes[__stoq_data_"]').forEach(
      (input) => input.remove(),
    );
  }

  async function retryWithoutSellingPlan(url, init, response) {
    if (!cartAddUrl(url) || init?.__omalleyStrippedSellingPlan) return response;

    let json = null;
    try {
      json = await response.clone().json();
    } catch {
      return response;
    }
    if (!isSellingPlanError(json, response.status)) return response;

    const stripped = stripBody(init && init.body);
    if (!stripped.changed) return response;

    const nextInit = Object.assign({}, init, {
      body: stripped.body,
      __omalleyStrippedSellingPlan: true,
    });
    return nativeFetch(url, nextInit);
  }

  function installFetchGuard() {
    if (window.fetch && window.fetch.__omalleySellingPlanGuard) return;
    const currentFetch = window.fetch.bind(window);
    const guarded = async function (input, init) {
      const url = requestUrl(input);
      const response = await currentFetch(input, init);
      return retryWithoutSellingPlan(url, init, response);
    };
    guarded.__omalleySellingPlanGuard = true;
    window.fetch = guarded;
  }

  function install() {
    installFetchGuard();
    stripInjectedInputs();
  }

  install();
  document.addEventListener('DOMContentLoaded', install);
  document.addEventListener('submit', stripInjectedInputs, true);
  setTimeout(install, 0);
  setTimeout(install, 1200);
})();
